import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PDFCover from '../../../components/PDFCover/PDFCover';
import AxiosInstance from '../../../helpers/AxiosInstance';
import './ElMadjlisHCI.css';

const HCI_PDF_BASE = '/api/visitor/hci-books/pdf';
/** Build API URL for a category cover image (category id = folder name, may contain spaces/Arabic). */
const HCI_COVER_IMAGE_URL = (categoryId, filename) =>
  `/api/visitor/hci-covers/${encodeURIComponent(categoryId)}/${encodeURIComponent(filename)}`;

const CATEGORY_IDS = ['cat1', 'cat2', 'cat3', 'cat4'];

const ElMadjlisHCI = () => {
  const { t } = useTranslation();
  const [coverErrors, setCoverErrors] = useState({});
  const [carouselBooks, setCarouselBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    AxiosInstance.get('visitor/hci-books')
      .then((list) => {
        setCarouselBooks(Array.isArray(list) ? list : []);
      })
      .catch(() => setCarouselBooks([]))
      .finally(() => setBooksLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;
    const onEscape = (e) => { if (e.key === 'Escape') setSelectedCategory(null); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [selectedCategory]);

  useEffect(() => {
    AxiosInstance.get('visitor/hci-categories')
      .then((res) => {
        const list = Array.isArray(res) ? res : (res && res.data);
        const items = Array.isArray(list) && list.length >= 4 ? list : null;
        if (items) {
          setCategories(items);
        } else {
          setCategories(CATEGORY_IDS.map((id) => ({ id, name: t(`elMadjlisHCI.category.${id}`), images: [] })));
        }
      })
      .catch(() => {
        setCategories(CATEGORY_IDS.map((id) => ({ id, name: t(`elMadjlisHCI.category.${id}`), images: [] })));
      })
      .finally(() => setCategoriesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount; t() used only for fallback
  }, []);

  const handleCoverError = (catId, filename) => {
    setCoverErrors((prev) => ({ ...prev, [`${catId}-${filename}`]: true }));
  };

  const selectedCat = selectedCategory ? categories.find((c) => c.id === selectedCategory) : null;

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    responsive: [
      { breakpoint: 992, settings: { slidesToShow: 2 } },
      { breakpoint: 576, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <div className="el-madjlis-hci-page">
      <div className="container mt-4 mb-5">
        {/* Hero section: logo left, title & subtitle right */}
        <div className="hci-hero-section">
          <div className="hci-hero-logo-wrap">
            <img src="/assets/images/hci/logoHCI.png" alt="" className="hci-hero-logo" />
          </div>
          <div className="hci-hero-content">
            <h1 className="display-4 fw-bold mb-3">
              <i className="fas fa-mosque me-3"></i>
              {t('elMadjlisHCI.heroTitle')}
            </h1>
            <p className="lead mb-0">{t('elMadjlisHCI.heroSubtitle')}</p>
          </div>
        </div>

        {/* Carousel of books: first page of PDF as cover, click opens PDF */}
        <section className="hci-carousel-section mt-5">
          <h2 className="hci-section-title mb-4">{t('elMadjlisHCI.carouselTitle')}</h2>
          {booksLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : carouselBooks.length === 0 ? (
            <p className="text-muted text-center">{t('elMadjlisHCI.noBooks')}</p>
          ) : (
            <Slider {...carouselSettings} className="hci-books-carousel">
              {carouselBooks.map((book) => (
                <div key={book.id} className="hci-carousel-item px-2">
                  <a
                    href={`${HCI_PDF_BASE}/${book.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hci-carousel-card hci-carousel-card-link"
                    title={book.title}
                  >
                    <div className="hci-carousel-pdf-cover-wrap">
                      {book.coverImage ? (
                        <img
                          src={HCI_COVER_IMAGE_URL(book.coverImage.categoryId, book.coverImage.filename)}
                          alt={book.title}
                          className="hci-carousel-pdf-cover hci-carousel-cover-img"
                        />
                      ) : (
                        <PDFCover
                          pdfUrl={`${HCI_PDF_BASE}/${book.id}`}
                          width={200}
                          height={283}
                          className="hci-carousel-pdf-cover"
                          alt={book.title}
                          lazy={true}
                          fitWidthOnly={true}
                        />
                      )}
                    </div>
                    <div className="hci-carousel-pdf-badge">
                      <i className="fas fa-file-pdf me-1"></i>
                      {t('elMadjlisHCI.openPdf')}
                    </div>
                    <div className="hci-carousel-book-title">{book.title}</div>
                  </a>
                </div>
              ))}
            </Slider>
          )}
        </section>

        {/* 4 category cards next to each other – name only; click opens catalog of covers */}
        <section className="hci-categories-section mt-5">
          <h2 className="hci-section-title mb-4">{t('elMadjlisHCI.categoriesTitle')}</h2>
          {categoriesLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {categories.map((cat) => (
                <div key={cat.id} className="col-md-6 col-lg-3">
                  <button
                    type="button"
                    className="hci-category-card hci-category-card-name card w-100 h-100 border-0 shadow-sm"
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    <div className="card-body d-flex align-items-center justify-content-center">
                      <h5 className="card-title hci-category-title mb-0 text-center">{cat.name}</h5>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Modal: catalog of covers for selected category */}
          {selectedCat && (
            <div className="hci-catalog-modal-backdrop" onClick={() => setSelectedCategory(null)} role="dialog" aria-modal="true" aria-label={selectedCat?.name}>
              <div className="hci-catalog-modal" onClick={(e) => e.stopPropagation()}>
                <div className="hci-catalog-modal-header">
                  <h3 className="hci-catalog-modal-title">{selectedCat.name}</h3>
                  <button type="button" className="hci-catalog-modal-close btn btn-link" onClick={() => setSelectedCategory(null)} aria-label="Close">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="hci-catalog-modal-body">
                  {selectedCat.images.length === 0 ? (
                    <div className="hci-cover-placeholder hci-cover-placeholder-inline">
                      <i className="fas fa-book-open"></i>
                      <span>{t('elMadjlisHCI.coverPlaceholder')}</span>
                    </div>
                  ) : (
                    <div className="row g-3 hci-category-covers">
                      {(() => {
                        const withPdf = (selectedCat.images || []).filter((f) => selectedCat.coverPdfLinks?.[f]);
                        const withoutPdf = (selectedCat.images || []).filter((f) => !selectedCat.coverPdfLinks?.[f]);
                        const displayOrder = withPdf.length ? [...withPdf.sort((a, b) => a.localeCompare(b, 'ar')), ...withoutPdf] : selectedCat.images;
                        return displayOrder;
                      })().map((filename) => {
                        const bookId = selectedCat.coverPdfLinks?.[filename];
                        const cardContent = (
                          <div className="hci-category-card card h-100 border-0 shadow-sm">
                            <div className="hci-category-cover-wrap">
                              {coverErrors[`${selectedCat.id}-${filename}`] ? (
                                <div className="hci-cover-placeholder">
                                  <i className="fas fa-book-open"></i>
                                  <span>{t('elMadjlisHCI.coverPlaceholder')}</span>
                                </div>
                              ) : (
                                <img
                                  src={HCI_COVER_IMAGE_URL(selectedCat.id, filename)}
                                  alt={`${selectedCat.name} - ${filename}`}
                                  onError={() => handleCoverError(selectedCat.id, filename)}
                                  className="hci-category-cover"
                                />
                              )}
                            </div>
                            {bookId != null && (
                              <div className="hci-category-pdf-badge">
                                <i className="fas fa-file-pdf me-1"></i>
                                {t('elMadjlisHCI.openPdf')}
                              </div>
                            )}
                          </div>
                        );
                        return (
                          <div key={filename} className="col-6 col-md-4 col-lg-3">
                            {bookId != null ? (
                              <a
                                href={`${HCI_PDF_BASE}/${bookId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hci-category-cover-link"
                                title={t('elMadjlisHCI.openPdf')}
                              >
                                {cardContent}
                              </a>
                            ) : (
                              cardContent
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ElMadjlisHCI;
